import React, { FC, ReactNode, useMemo } from "react";
import { useSelector } from "react-redux";
import { createTheme, ThemeProvider } from "@mui/material/styles";
import { palette, radius, readableOn, shadow, withAlpha } from "./tokens";

// The app-wide MUI theme (the older DefaultTheme was never applied). Built from the vendor config so every MUI
// component picks up the brand colour, with the shared modern look: light page, white bordered cards, pill buttons and
// inputs, soft rounded dialogs and drawers.

const FONT = `customFont, "Inter", "Segoe UI", Roboto, "Helvetica Neue", Arial, sans-serif`;

export const buildTheme = (primary: string, secondary: string) => {
  const brand = /^#[0-9a-f]{6}$/i.test(primary ?? "") ? primary : "#3c44b1";
  const second = /^#[0-9a-f]{6}$/i.test(secondary ?? "") ? secondary : palette.ink;

  return createTheme({
    palette: {
      primary: { main: brand, contrastText: readableOn(brand) },
      secondary: { main: second, contrastText: readableOn(second) },
      success: { main: palette.success },
      error: { main: palette.danger },
      warning: { main: palette.warning },
      info: { main: palette.info },
      background: { default: palette.page, paper: palette.surface },
      text: { primary: palette.text, secondary: palette.textMuted },
      divider: palette.border,
    },
    shape: { borderRadius: radius.md },
    typography: {
      fontFamily: FONT,
      button: { textTransform: "none", fontWeight: 600 },
      h1: { fontWeight: 700 },
      h2: { fontWeight: 700 },
      h3: { fontWeight: 700 },
      h4: { fontWeight: 700 },
      h5: { fontWeight: 700 },
      h6: { fontWeight: 700 },
    },
    breakpoints: { values: { xs: 0, sm: 576, md: 768, lg: 1100, xl: 1381 } },
    components: {
      MuiCssBaseline: {
        styleOverrides: { body: { backgroundColor: palette.page, color: palette.text } },
      },
      MuiButton: {
        defaultProps: { disableElevation: true },
        styleOverrides: {
          root: { borderRadius: radius.pill, paddingLeft: 20, paddingRight: 20, minHeight: 42, fontWeight: 600 },
          sizeSmall: { minHeight: 34, paddingLeft: 14, paddingRight: 14 },
          sizeLarge: { minHeight: 50 },
          outlined: { borderWidth: 1.5, "&:hover": { borderWidth: 1.5 } },
        },
      },
      MuiIconButton: { styleOverrides: { root: { borderRadius: radius.pill } } },
      MuiOutlinedInput: {
        styleOverrides: {
          root: {
            borderRadius: radius.pill,
            backgroundColor: palette.surface,
            "& .MuiOutlinedInput-notchedOutline": { borderColor: palette.borderStrong },
            "&:hover .MuiOutlinedInput-notchedOutline": { borderColor: brand },
          },
          input: { paddingLeft: 18, paddingRight: 18 },
          multiline: { borderRadius: radius.lg },
        },
      },
      MuiCard: {
        styleOverrides: {
          root: { borderRadius: radius.lg, border: `1px solid ${palette.border}`, boxShadow: shadow.card, backgroundImage: "none" },
        },
      },
      MuiPaper: { styleOverrides: { rounded: { borderRadius: radius.lg }, root: { backgroundImage: "none" } } },
      // Rounded dialogs like the log in dialog; full-screen dialogs (e.g. the page loader) stay square.
      MuiDialog: {
        styleOverrides: {
          paper: { borderRadius: radius.xl, boxShadow: shadow.float },
          paperFullScreen: { borderRadius: 0 },
        },
      },
      MuiDialogTitle: { styleOverrides: { root: { fontSize: 20, fontWeight: 700, color: palette.text, padding: "24px 24px 8px" } } },
      MuiDialogContent: { styleOverrides: { root: { padding: "8px 24px", color: palette.textMuted } } },
      MuiDialogContentText: { styleOverrides: { root: { color: palette.textMuted, fontSize: 14 } } },
      MuiDialogActions: { styleOverrides: { root: { padding: "16px 24px 24px", gap: 8, "& > :not(:first-of-type)": { marginLeft: 0 } } } },
      MuiDrawer: { styleOverrides: { paper: { backgroundColor: palette.page } } },
      MuiMenu: { styleOverrides: { paper: { borderRadius: radius.lg, border: `1px solid ${palette.border}`, boxShadow: shadow.float } } },
      MuiPopover: { styleOverrides: { paper: { borderRadius: radius.lg } } },
      MuiChip: { styleOverrides: { root: { borderRadius: radius.pill, fontWeight: 600 } } },
      MuiSkeleton: {
        defaultProps: { animation: "wave" },
        styleOverrides: { root: { backgroundColor: palette.surfaceMuted }, rounded: { borderRadius: radius.md }, text: { borderRadius: radius.pill } },
      },
      MuiTab: { styleOverrides: { root: { textTransform: "none", fontWeight: 600 } } },
      MuiTooltip: { styleOverrides: { tooltip: { borderRadius: radius.sm, backgroundColor: palette.ink } } },
      MuiAlert: { styleOverrides: { root: { borderRadius: radius.lg } } },
      MuiPaginationItem: { styleOverrides: { root: { borderRadius: radius.pill } } },
      MuiToggleButton: { styleOverrides: { root: { borderRadius: radius.pill, textTransform: "none" } } },
      MuiAccordion: { styleOverrides: { root: { borderRadius: radius.lg, "&:before": { display: "none" } } } },
      MuiLinearProgress: { styleOverrides: { root: { borderRadius: radius.pill, backgroundColor: withAlpha(brand, 0.15) } } },
    },
  });
};

export const AppThemeProvider: FC<{ children: ReactNode }> = ({ children }) => {
  const styles = useSelector((state: any) => state.settings?.settings?.Styles);
  const theme = useMemo(() => buildTheme(styles?.Primary, styles?.Secondary), [styles?.Primary, styles?.Secondary]);
  return <ThemeProvider theme={theme}>{children}</ThemeProvider>;
};
